import React, { useEffect, useRef } from 'react';
import TextInput, { TextInputProps } from '../TextInput';
import IconButton from '../IconButton';
import { SearchIcon, CloseIcon } from '../IconButton/icons';

export interface SearchInputProps extends Omit<TextInputProps, 'size' | 'trailing'> {
  /** Shown as an × inside the field while there is a value. */
  onClear?: () => void;
  /** Pressing `/` anywhere on the page (outside another field) focuses this input. Default on. */
  slashShortcut?: boolean;
}

const isTypingTarget = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
};

/**
 * The command bar's search: a 32px filled pill with a leading magnifier, a
 * trailing clear button and a `/` key hint. Every native input prop passes
 * through to TextInput.
 */
const SearchInput: React.FC<SearchInputProps> = ({
  onClear,
  slashShortcut = true,
  value,
  className = '',
  ...rest
}) => {
  const input = useRef<HTMLInputElement>(null);
  const hasValue = typeof value === 'string' ? value.length > 0 : Array.isArray(value) ? value.length > 0 : value != null;

  useEffect(() => {
    if (!slashShortcut) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      // A modal on top of the page owns the keyboard.
      if (document.querySelector('.modal-overlay')) return;
      e.preventDefault();
      input.current?.focus();
      input.current?.select();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [slashShortcut]);

  return (
    <div className={`search-field ${hasValue ? 'has-value' : ''} ${className}`.trim()}>
      <span className="search-field__icon" aria-hidden="true">
        <SearchIcon />
      </span>
      <TextInput
        ref={input}
        type="search"
        value={value}
        className="search-field__input"
        onKeyDown={(e) => {
          if (e.key === 'Escape') input.current?.blur();
          rest.onKeyDown?.(e);
        }}
        {...rest}
      />
      {onClear && hasValue ? (
        <IconButton
          icon={<CloseIcon />}
          label="Clear search"
          size="sm"
          className="search-field__clear"
          onClick={onClear}
        />
      ) : (
        slashShortcut && (
          <kbd className="search-field__kbd" aria-hidden="true">
            /
          </kbd>
        )
      )}
    </div>
  );
};

export default SearchInput;
